import { Button } from "@/components/ui/button";
import { scrollToTop } from "@/utils";
import { Mail, MapPin, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";

const WelcomeGarizimRiveSud = () => {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    scrollToTop();
    navigate(path);
  };

  return (
    <section className="relative bg-slate-950 text-white overflow-hidden border-b border-slate-900">
      {/* Ambient Architectural Lighting */}
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-sky-600/10 rounded-full blur-3xl pointer-events-none" />

      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:24px_24px] opacity-25 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8 sm:pt-32 sm:pb-16 lg:py-24 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10 lg:gap-8 items-center">
          {/* Left Column: Visual & Typographic Anchor (Cols 1 to 7) */}
          <div className="lg:col-span-7 text-left space-y-3 sm:space-y-6">
            {/* Campus Micro-Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-3.5 sm:py-1.5 rounded-full bg-slate-900/90 border border-slate-800 text-slate-300 text-[10px] sm:text-xs font-semibold tracking-wider uppercase max-w-full truncate">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
              <span className="truncate">Campus Sud • Longueuil</span>
            </div>

            {/* Main Typographic Headline */}
            <div className="space-y-0.5 sm:space-y-1">
              <h1 className="text-2xl xs:text-3xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.08] text-white">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-10 to-amber-500">
                  RIVE-SUD
                </span>
              </h1>
            </div>

            {/* Editorial Mission Statement */}
            <div className="border-l-2 border-amber-400/80 pl-3 sm:pl-4 py-0.5">
              <p className="font-serif italic text-sm sm:text-xl text-slate-200 leading-snug sm:leading-relaxed font-normal">
                « Un lieu d’édification, de fraternité et de prière au cœur de
                la Rive-Sud. »
              </p>
            </div>

            {/* Welcoming Narrative */}
            <p className="text-xs sm:text-base lg:text-lg text-slate-300 max-w-xl leading-relaxed line-clamp-2 sm:line-clamp-none">
              Le campus Garizim Rive-Sud accueille chaleureusement les croyants
              et les familles de Longueuil et des environs pour vivre des temps
              forts de louange et de communion fraternelle.
            </p>

            {/* Primary Action Buttons */}
            <div className="grid grid-cols-2 sm:flex sm:flex-row items-center gap-2 sm:gap-3 pt-1 sm:pt-2 w-full sm:w-auto">
              {/* <Button
                size="default"
                className="w-full sm:w-auto bg-white hover:bg-slate-100 text-slate-950 font-bold px-3 sm:px-7 py-2 sm:py-3 rounded-full text-xs sm:text-base shadow-md shadow-white/10 transition-all hover:scale-[1.02] justify-center h-auto"
                onClick={() => {
                  const el = document.getElementById("services-rive-sud");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-slate-950 shrink-0" />
                <span>Culte 14h00</span>
              </Button> */}

              <Button
                variant="outline"
                size="default"
                className="w-full sm:w-auto border-slate-700 bg-slate-900/60 hover:bg-slate-800 text-slate-200 hover:text-white font-semibold px-3 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-base backdrop-blur transition-all justify-center h-auto"
                onClick={() => handleNavigate("/")}
              >
                <span className="truncate">Montréal &rarr;</span>
              </Button>
            </div>
          </div>

          {/* Right Column: Gathering Hub Card (Cols 8 to 12) */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-slate-900/100 border border-slate-800/90 p-4 sm:p-8 shadow-2xl backdrop-blur-xl relative space-y-3.5 sm:space-y-6">
              {/* Header: Service Schedule */}
              <div className="flex items-start justify-between border-b border-slate-800/80 pb-3.5 sm:pb-5">
                <div>
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-widest font-bold text-amber-400 block mb-0.5">
                    Culte Rive-Sud
                  </span>
                  <h3 className="text-lg sm:text-2xl font-black text-white tracking-tight">
                    Service Dominical
                  </h3>
                  <p className="text-slate-400 text-xs sm:text-sm mt-0.5">
                    Tous les dimanches après-midi
                  </p>
                </div>
                <div className="text-right bg-slate-800/80 px-2.5 py-1 sm:px-3.5 sm:py-2 rounded-xl border border-slate-700/60">
                  <span className="text-[9px] sm:text-xs text-slate-400 uppercase tracking-wider block font-semibold">
                    Heure
                  </span>
                  <span className="text-sm sm:text-lg font-extrabold text-amber-300">
                    14h00
                  </span>
                </div>
              </div>

              {/* Location Detail */}

              <div className="space-y-2.5 sm:space-y-3">
                {/* 
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-slate-800/80 border border-slate-700/70 flex items-center justify-center shrink-0 text-slate-300 mt-0.5">
                    <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
                  </div>
                 <div className="flex-1 text-xs sm:text-sm">
                    <p className="font-semibold text-white">
                      Groupe Biblique de la Rive-Sud
                    </p>
                    <p className="text-slate-400 text-xs mt-0.5">
                      530 Rue Saint-Jean, Longueuil, QC J4H 2Y4
                    </p>
                  </div> 
                </div>*/}

                {/* Direct Contact */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-800/60 text-xs">
                  <a
                    href="tel:514-322-4921"
                    className="flex items-center justify-center sm:justify-start gap-1.5 p-2 sm:p-2.5 rounded-xl bg-slate-950/60 hover:bg-slate-800/60 border border-slate-800/80 text-slate-300 transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="font-mono truncate">(514) 322-4921</span>
                  </a>
                  <div className="flex items-center justify-center sm:justify-start gap-1.5 p-2 sm:p-2.5 rounded-xl bg-slate-950/60 border border-slate-800/80 text-slate-300 min-w-0">
                    <Mail className="w-3.5 h-3.5 text-amber-400/90 shrink-0" />
                    <span className="truncate">egarizim@gmail.com</span>
                  </div>
                </div>
              </div>

              {/* Map CTA Button */}
              <div className="pt-1 sm:pt-2">
                <Button
                  className="w-full bg-slate-800 hover:bg-slate-700 text-white font-semibold py-2.5 sm:py-3 rounded-xl border border-slate-700 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm"
                  onClick={() =>
                    window.open(
                      "https://share.google/4Xg2v1gYUpSKD8KiU",
                      "_blank",
                    )
                  }
                >
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
                  <span>530 Rue Saint-Jean, Longueuil, J4H2Y4</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WelcomeGarizimRiveSud;
